import { Bell, Search, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SearchDialog from '@/components/SearchDialog';
import { NotificationsDialog } from '@/components/NotificationsDialog';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { useAppBadge } from '@/hooks/useAppBadge';
import { getProfileBasicHeader } from '@/data/raw';
import { STORAGE_KEYS } from '@/config';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const MobileHeader = () => {
  const [openSearch, setOpenSearch] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const { unreadCount } = useNotifications();
  const { clearBadge } = useAppBadge(unreadCount);
  const { session } = useAuth();
  const isGuest = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEYS.GUEST) === "true";
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const initials = (name?: string | null) => (name?.slice(0,2).toUpperCase() || 'BR');

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!session?.user?.id) {
        if (active) { setAvatarUrl(null); setDisplayName(null); }
        return;
      }
      const { data } = await getProfileBasicHeader(session.user.id);
      if (active) {
        setAvatarUrl((data as any)?.avatar_url ?? null);
        setDisplayName((data as any)?.display_name ?? null);
      }
    };
    load();
    return () => { active = false; };
  }, [session?.user?.id]);
  return (
    <>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="mx-auto w-full max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl px-5 md:px-10 lg:px-24 xl:px-32 2xl:px-48 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <NavLink to={session ? "/home" : "/"} aria-label="BRRGR Home" className="select-none">
              <span className="font-extrabold uppercase tracking-wide text-primary text-xl leading-none">BRRGR</span>
            </NavLink>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full" aria-label="Search" onClick={() => setOpenSearch(true)}>
              <Search className="h-5 w-5" />
            </Button>
            {!session?.user?.id ? (
              // Unauthenticated users see sign up CTA
              <Button asChild variant="default" size="sm" className="bg-primary hover:bg-primary/90">
                <NavLink to="/auth">Sign Up</NavLink>
              </Button>
            ) : (
              // Authenticated users see notifications and profile
              <>
                {!isGuest && (
                  <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full" aria-label="Notifications" onClick={() => { setOpenNotifications(true); clearBadge(); }}>
                    <div className="relative">
                      <Bell className="h-5 w-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] leading-4 text-center">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                  </Button>
                )}
                {!isGuest && (
                  <Button asChild variant="ghost" size="icon" className="h-10 w-10 rounded-full" aria-label="Profile">
                    <NavLink to="/profile">
                      {avatarUrl ? (
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={avatarUrl ?? undefined} alt={displayName ?? 'Profile'} />
                          <AvatarFallback>{initials(displayName)}</AvatarFallback>
                        </Avatar>
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                    </NavLink>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>
      <SearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <NotificationsDialog open={openNotifications} onOpenChange={setOpenNotifications} />
    </>
  );
};

export default MobileHeader;